import Component from '@glimmer/component';
import { Heading, Text } from 'tio-ui/components/typography';
import {
  PhShieldCheck,
  PhFileText,
  PhArrowsClockwise,
  PhNewspaper,
  PhCheckCircle,
  PhWarning,
  PhSpinner,
  PhLightning,
} from 'ember-phosphor-icons';
import { eq } from 'tio-ui/utilities';
import type {
  PslfAutomationStatus,
  PslfProtectionStatus,
} from 'tio-employee/services/career-pathways-api';

interface ControlTowerSignature {
  Args: {
    automationStatus: PslfAutomationStatus;
    protectionStatus: PslfProtectionStatus;
  };
  Element: HTMLDivElement;
}

// Helper to get status icon and color
function getStatusDisplay(status: string): {
  icon: typeof PhCheckCircle;
  color: string;
  bgColor: string;
} {
  switch (status) {
    case 'automated':
    case 'synced':
    case 'protected':
      return { icon: PhCheckCircle, color: 'text-emerald-400', bgColor: 'bg-emerald-500/20' };
    case 'easy_mode':
      return { icon: PhLightning, color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' };
    case 'syncing':
    case 'monitoring':
      return { icon: PhSpinner, color: 'text-blue-400', bgColor: 'bg-blue-500/20' };
    case 'action_needed':
    case 'alert':
    case 'expired':
    case 'stale':
    case 'error':
      return { icon: PhWarning, color: 'text-red-400', bgColor: 'bg-red-500/20' };
    default:
      return { icon: PhCheckCircle, color: 'text-gray-400', bgColor: 'bg-gray-500/20' };
  }
}

export default class PslfControlTower extends Component<ControlTowerSignature> {
  get ecfDisplay() {
    return getStatusDisplay(this.args.automationStatus.ecfStatus);
  }

  get idrDisplay() {
    return getStatusDisplay(this.args.automationStatus.idrStatus);
  }

  get syncDisplay() {
    return getStatusDisplay(this.args.automationStatus.syncStatus);
  }

  get policyDisplay() {
    return getStatusDisplay(this.args.automationStatus.policyStatus);
  }

  get threatLevelColor() {
    switch (this.args.protectionStatus.threatLevel) {
      case 'low':
        return 'emerald';
      case 'medium':
        return 'amber';
      case 'high':
        return 'red';
      default:
        return 'gray';
    }
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 rounded-2xl p-6 text-white shadow-xl"
      data-test-control-tower
      ...attributes
    >
      {{! Header }}
      <div class="flex items-center gap-3 mb-6">
        <div class="p-2 bg-violet-500/20 rounded-xl">
          <span class="text-2xl">🎛️</span>
        </div>
        <div>
          <Heading @level={{2}} class="text-white text-xl font-bold">
            PSLF Control Tower
          </Heading>
          <Text class="text-gray-400 text-sm">Your autopilot status</Text>
        </div>
      </div>

      {{! Status Modules }}
      <div class="space-y-3">
        {{! Employer Certification }}
        <div class="bg-white/5 rounded-xl p-4 border border-white/10" data-test-ecf-section>
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="p-2 {{this.ecfDisplay.bgColor}} rounded-lg">
                <PhShieldCheck @size={{20}} class="{{this.ecfDisplay.color}}" />
              </div>
              <div>
                <Text class="text-white font-medium">Employer Certification</Text>
                <Text class="text-gray-400 text-sm">{{@automationStatus.ecfDetails}}</Text>
              </div>
            </div>
            <div
              class="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                {{if
                  (eq @automationStatus.ecfStatus 'automated')
                  'bg-emerald-500/20 text-emerald-400'
                }}
                {{if (eq @automationStatus.ecfStatus 'manual') 'bg-gray-500/20 text-gray-400'}}
                {{if
                  (eq @automationStatus.ecfStatus 'action_needed')
                  'bg-red-500/20 text-red-400'
                }}"
              data-test-ecf-status
            >
              {{@automationStatus.ecfStatusLabel}}
            </div>
          </div>
          {{#if @automationStatus.ecfLastSubmitted}}
            <div class="mt-2 text-xs text-gray-500" data-test-ecf-last-submitted>
              Last submitted:
              {{@automationStatus.ecfLastSubmitted}}
            </div>
          {{/if}}
        </div>

        {{! IDR Recertification }}
        <div class="bg-white/5 rounded-xl p-4 border border-white/10" data-test-idr-section>
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="p-2 {{this.idrDisplay.bgColor}} rounded-lg">
                <PhFileText @size={{20}} class="{{this.idrDisplay.color}}" />
              </div>
              <div>
                <Text class="text-white font-medium">IDR Recertification</Text>
                <Text class="text-gray-400 text-sm">{{@automationStatus.idrDetails}}</Text>
              </div>
            </div>
            <div
              class="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                {{if
                  (eq @automationStatus.idrStatus 'automated')
                  'bg-emerald-500/20 text-emerald-400'
                }}
                {{if
                  (eq @automationStatus.idrStatus 'easy_mode')
                  'bg-yellow-500/20 text-yellow-400'
                }}
                {{if (eq @automationStatus.idrStatus 'action_needed') 'bg-red-500/20 text-red-400'}}
                {{if (eq @automationStatus.idrStatus 'expired') 'bg-red-500/20 text-red-400'}}"
              data-test-idr-status
            >
              {{@automationStatus.idrStatusLabel}}
            </div>
          </div>
          {{#if @automationStatus.idrPlanExpires}}
            <div class="mt-2 text-xs text-gray-500" data-test-idr-plan-expires>
              Current plan expires:
              {{@automationStatus.idrPlanExpires}}
            </div>
          {{/if}}
        </div>

        {{! Payment Verification }}
        <div class="bg-white/5 rounded-xl p-4 border border-white/10" data-test-sync-section>
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="p-2 {{this.syncDisplay.bgColor}} rounded-lg">
                <PhArrowsClockwise
                  @size={{20}}
                  class="{{this.syncDisplay.color}}
                    {{if (eq @automationStatus.syncStatus 'syncing') 'animate-spin'}}"
                />
              </div>
              <div>
                <Text class="text-white font-medium">Payment Verification</Text>
                <Text class="text-gray-400 text-sm">{{@automationStatus.syncDetails}}</Text>
              </div>
            </div>
            <div
              class="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                {{if
                  (eq @automationStatus.syncStatus 'synced')
                  'bg-emerald-500/20 text-emerald-400'
                }}
                {{if (eq @automationStatus.syncStatus 'syncing') 'bg-blue-500/20 text-blue-400'}}
                {{if (eq @automationStatus.syncStatus 'stale') 'bg-amber-500/20 text-amber-400'}}
                {{if (eq @automationStatus.syncStatus 'error') 'bg-red-500/20 text-red-400'}}"
              data-test-sync-status
            >
              {{@automationStatus.syncStatusLabel}}
            </div>
          </div>
          <div class="mt-2 text-xs text-gray-500" data-test-sync-last-updated>
            Last sync:
            {{@automationStatus.syncLastUpdated}}
          </div>
        </div>

        {{! Policy Monitor }}
        <div class="bg-white/5 rounded-xl p-4 border border-white/10" data-test-policy-section>
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="p-2 {{this.policyDisplay.bgColor}} rounded-lg">
                <PhNewspaper @size={{20}} class="{{this.policyDisplay.color}}" />
              </div>
              <div>
                <Text class="text-white font-medium">Policy Monitor</Text>
                <Text class="text-gray-400 text-sm">{{@automationStatus.policyDetails}}</Text>
              </div>
            </div>
            <div
              class="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                {{if
                  (eq @automationStatus.policyStatus 'protected')
                  'bg-emerald-500/20 text-emerald-400'
                }}
                {{if
                  (eq @automationStatus.policyStatus 'monitoring')
                  'bg-blue-500/20 text-blue-400'
                }}
                {{if (eq @automationStatus.policyStatus 'alert') 'bg-red-500/20 text-red-400'}}"
              data-test-policy-status
            >
              {{@automationStatus.policyStatusLabel}}
            </div>
          </div>
        </div>
      </div>

      {{! Threat Level Indicator }}
      <div class="mt-6 pt-4 border-t border-white/10">
        <div class="flex items-center justify-between">
          <Text class="text-gray-400 text-sm">Overall Protection Level</Text>
          <div class="flex items-center gap-2">
            <div
              class="w-3 h-3 rounded-full
                {{if (eq this.threatLevelColor 'emerald') 'bg-emerald-500'}}
                {{if (eq this.threatLevelColor 'amber') 'bg-amber-500'}}
                {{if (eq this.threatLevelColor 'red') 'bg-red-500'}}"
            ></div>
            <Text
              class="font-bold uppercase text-sm
                {{if (eq this.threatLevelColor 'emerald') 'text-emerald-400'}}
                {{if (eq this.threatLevelColor 'amber') 'text-amber-400'}}
                {{if (eq this.threatLevelColor 'red') 'text-red-400'}}"
              data-test-threat-level
            >
              {{@protectionStatus.threatLevel}}
              RISK
            </Text>
          </div>
        </div>
        {{#if @protectionStatus.grandfathered}}
          <Text class="text-emerald-400 text-xs mt-2" data-test-grandfathered>
            🛡️ Grandfathered since
            {{@protectionStatus.grandfatheredSince}}
            — your eligibility is protected
          </Text>
        {{/if}}
      </div>
    </div>
  </template>
}
